import { AxiosError } from 'axios';
import { find } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { banksApi } from 'api';
import {
  DataGridColumnDefinition,
  CrudTableActionType,
  CloseFormikDialogData,
  dataGridColumns,
  CrudTableAction,
  CrudPage,
} from 'components';
import { FilterDefinitionType, QueryKey } from 'enums';
import { useMutation, usePartialQuery, useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Bank, BanksFilters, FilterDefinition } from 'types';
import { formUtils, validationUtils } from 'utils';

import { BankDetailsDialog } from './BankDetailsDialog';

type Props = {
  archived?: boolean;
};

export const BanksList: React.FC<Props> = ({ archived }) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.banks',
  });

  const { banks, banksRaw } = useUserContext();

  const queryClient = useQueryClient();

  const queryResult = usePartialQuery(
    QueryKey.BanksNotArchive,
    banksApi.getAllNotArchive,
    {
      enabled: !archived,
    },
  );
  const queryResultArchive = usePartialQuery(
    QueryKey.BanksArchive,
    banksApi.getAllArchivePaginated,
    {
      enabled: !!archived,
    },
  );

  const invalidateBanksQueries = useCallback(() => {
    queryClient.invalidateQueries(QueryKey.BanksArchive);
    queryClient.invalidateQueries(QueryKey.BanksNotArchive);
    queryClient.invalidateQueries(QueryKey.Banks);
  }, [queryClient]);

  const invalidateTradeMethodsQueries = useCallback(() => {
    queryClient.invalidateQueries(QueryKey.TradeMethodsArchive);
    queryClient.invalidateQueries(QueryKey.TradeMethodsNotArchive);
    queryClient.invalidateQueries(QueryKey.TradeMethods);
  }, [queryClient]);

  const { mutate: archive } = useMutation(banksApi.archive, {
    onSuccess: () => {
      invalidateBanksQueries();
      invalidateTradeMethodsQueries();
    },
    notifierType: 'remove',
  });

  const { mutate: restore } = useMutation(banksApi.restore, {
    onSuccess: () => {
      invalidateBanksQueries();
    },
    notifierType: 'execute',
  });

  const { mutate: update } = useMutation<
    Bank,
    AxiosError,
    { id: string; data: Partial<Bank> }
  >(banksApi.update);

  const columns: DataGridColumnDefinition<Bank>[] = useMemo(
    () => [
      dataGridColumns.getIdColumn(),
      {
        header: t('fields.code'),
        valueKey: 'code',
      },
      {
        header: t('fields.name'),
        valueKey: 'name',
      },
    ],
    [t],
  );

  const handleUpdate = useCallback(
    (
      item: Bank,
      data: CloseFormikDialogData<Partial<Bank>>,
      closeDialog: () => void,
    ) => {
      update(
        { id: item.id, data: data?.values },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(QueryKey.Banks);
            queryClient.invalidateQueries(QueryKey.BanksNotArchive);
            queryClient.invalidateQueries(QueryKey.TradeMethodsNotArchive);
            closeDialog();
          },
          onError: (error: AxiosError) => {
            data?.formikHelpers.setErrors(validationUtils.getFormErrors(error));
          },
        },
      );
    },
    [queryClient, update],
  );

  const bankNames = useMemo(() => formUtils.getOptions(banks, 'name'), [banks]);

  const filtersDefinitions: FilterDefinition<BanksFilters>[] = useMemo(
    () => [
      {
        label: t('fields.code'),
        name: 'code',
        type: FilterDefinitionType.Text,
      },
      {
        label: t('fields.name'),
        name: 'id',
        type: FilterDefinitionType.Select,
        options: bankNames,
        getDisplayName: (id: string) => find(banksRaw, { id })?.name,
      },
    ],
    [t, bankNames, banksRaw],
  );

  const actions = useMemo(
    (): CrudTableAction<Bank>[] => [
      {
        type: CrudTableActionType.Details,
        renderDialog: BankDetailsDialog,
        onUpdate: handleUpdate,
      },
      {
        type: CrudTableActionType.Remove,
        onRemove: (item, { close }) => archive(item.id, { onSuccess: close }),
      },
    ],
    [handleUpdate, archive],
  );

  const archivedActions = useMemo(
    (): CrudTableAction<Bank>[] => [
      {
        type: CrudTableActionType.Restore,
        onRestore: (item) => {
          restore(item.id);
        },
      },
    ],
    [restore],
  );

  return (
    <CrudPage
      header={{ hide: true }}
      filters={{ filtersDefinitions }}
      table={{
        queryResult: archived ? queryResultArchive : queryResult,
        columns,
        actions: archived ? archivedActions : actions,
        paginated: archived,
        ignoreState: { refetchLoading: true },
      }}
    />
  );
};
